import useFireBaseAuthentication from '@src/Hooks/useFirebaseAuthentication';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import {useAppDispatch, useAppSelector} from '@src/Store/hooks';
import {toast} from 'react-toastify';
import useSignInSuccess from '../../SignIn/useSignInSuccess';
import {Routes} from '@src/Constant/routes';
import useTracking from '@src/Tracking/useTracking';
import {TraceTokEvents} from '@src/Tracking/catalog';
import {markSignupPending} from '@src/Tracking/utils/signupPending';
import {trackTealiumSignupSuccessful} from '@src/Tracking/tealiumEvents';
import {
  buildTraceConsentPayload,
  hasRequiredConsent,
  normalizeConsentPayload,
} from '@src/App/components/Consent/consentPayload.utils';
import {useSaveConsentMutation} from '@src/Api/TraceApi/traceApi';
import apiConfig from '@src/Configs/apiConfig';

const getErrorMessage = (error: unknown) => {
  const apiMessage = (error as {data?: {message?: string}})?.data?.message;
  const message = (error as {message?: string})?.message;
  return apiMessage || message || 'Unable to save consent preferences';
};

const isConsentEndpointConfigured = (): boolean => {
  return !!(
    apiConfig.traceConsentApiUrl &&
    apiConfig.traceConsentAuthorization &&
    apiConfig.traceConsentApiKey
  );
};

const useSignUpSubmit = (redirectUrl?: Routes | string) => {
  const authState = useAppSelector(state => state.appAuth);
  const dispatch = useAppDispatch();
  const {signUp} = useFireBaseAuthentication();
  const {onSuccess: onSignInSuccess} = useSignInSuccess(redirectUrl);
  const {emit} = useTracking();
  const [saveConsent] = useSaveConsentMutation();

  const submit = async (formData: any) => {
    try {
      const consentPayload = normalizeConsentPayload(formData);
      if (!hasRequiredConsent(consentPayload)) {
        toast.error('You must accept the Terms and Conditions to continue');
        return false;
      }

      const data = {
        ...formData,
        ...consentPayload,
        dateOfBirth: formData.dateOfBirthValue || formData.dateOfBirth,
        language:
          typeof formData.language === 'string' ? [formData.language] : [],
        'account type': authState.socialNetworkSource || 'email',
        password: formData.password ?? 'AZERTY1234',
      };

      // P3: interests_selected — fires if interests were chosen during signup
      const interests = (formData.interests || formData.interest || []) as
        | unknown[]
        | undefined;
      if (Array.isArray(interests) && interests.length > 0) {
        const count = interests.length;
        emit(TraceTokEvents.InterestsSelected, {
          interest_count_bucket:
            count <= 3 ? '1-3' : count <= 6 ? '4-6' : '7+',
          interest_group_set: interests.join(','),
        });
      }

      // Flag before creating the account so LayoutProvider's identify effect can
      // emit account_info_submitted with the real user_id (see signupPending).
      markSignupPending(true);
      const response = await signUp(data);

      if (response.registeredMember) {
        if (isConsentEndpointConfigured()) {
          try {
            await saveConsent(
              buildTraceConsentPayload(consentPayload, {
                firebaseUid:
                  response.registeredMember.firebaseUid || response.user?.uid,
                email:
                  response.registeredMember.email ||
                  response.user?.email ||
                  formData.email,
              }),
            ).unwrap();
          } catch (consentError) {
            console.warn('Unable to save registration consent', consentError);
            toast.warn(
              'Registration completed, but consent preferences could not be saved.',
            );
          }
        } else {
          console.debug('Consent endpoint not configured, skipping consent save');
        }
      }

      if (response.status === 'success') {
        dispatch(appAuthSlice.actions.resetAuth());
        trackTealiumSignupSuccessful({
          userId: response.registeredMember?.firebaseUid || response.user?.uid,
          accountType: authState.socialNetworkSource || 'email',
        });
        await onSignInSuccess();
        return true;
      } else {
        markSignupPending(false);
        toast.error(response.errorMessage);
        return false;
      }
    } catch (e) {
      markSignupPending(false);
      toast.error(getErrorMessage(e));
      return false;
    }
  };

  return {submit};
};

export default useSignUpSubmit;
